import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, ShoppingBag, Ruler } from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent } from "./ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";

const getImageUrl = (url) => {
  if (!url) return "https://images.pexels.com/photos/12025472/pexels-photo-12025472.jpeg";
  if (url.startsWith("http")) return url;
  if (url.startsWith("/api")) return `${BACKEND_URL}${url}`;
  return url;
};

const sizes = ["XS", "S", "M", "L", "XL", "XXL"];

export default function ProductModal({ product, brandColors, brandName, onClose }) {
  const [selectedColor, setSelectedColor] = useState(0);
  const [selectedSize, setSelectedSize] = useState(2); // Default to M
  const [showSizeGuide, setShowSizeGuide] = useState(false);
  const [imageError, setImageError] = useState(false);

  if (!product) return null;

  const imageUrl = imageError 
    ? "https://images.pexels.com/photos/12025472/pexels-photo-12025472.jpeg"
    : getImageUrl(product.image_url);

  return (
    <Dialog open={!!product} onOpenChange={() => onClose()}>
      <DialogContent className="sm:max-w-4xl p-0 overflow-hidden" data-testid="product-modal">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 z-10 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-lg hover:bg-gray-100 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="grid md:grid-cols-2">
          {/* Image Section */}
          <div className="relative bg-gray-50 aspect-square md:aspect-auto">
            <img
              src={imageUrl}
              alt={product.name}
              className="w-full h-full object-cover"
              onError={() => setImageError(true)}
            />
            
            {/* Image Navigation (placeholder for multiple images) */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
              <div className="w-2 h-2 rounded-full bg-gray-900" />
              <div className="w-2 h-2 rounded-full bg-gray-300" />
              <div className="w-2 h-2 rounded-full bg-gray-300" />
            </div>
          </div>

          {/* Details Section */}
          <div className="p-8 overflow-y-auto max-h-[80vh]">
            <div className="space-y-6">
              {/* Header */}
              <div>
                <span className="text-sm text-gray-500 uppercase tracking-wide">
                  {product.category}
                </span>
                <h2 className="font-display text-2xl font-semibold text-gray-900 mt-1">
                  {product.name}
                </h2>
                <p className="text-3xl font-bold text-gray-900 mt-2">
                  {product.currency}{product.price}
                </p>
              </div>

              {/* Description */}
              <p className="text-gray-600 leading-relaxed">
                {product.description}
              </p>

              {/* Color Selection */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-900">Color</span>
                  <span className="text-sm text-gray-500">
                    {product.colors?.[selectedColor]}
                  </span>
                </div>
                <div className="flex gap-3">
                  {product.colors?.map((color, i) => (
                    <button
                      key={i}
                      onClick={() => setSelectedColor(i)}
                      className={`w-10 h-10 rounded-full border-2 transition-all ${
                        selectedColor === i 
                          ? "border-gray-900 ring-2 ring-gray-200" 
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                      style={{ backgroundColor: color }}
                      data-testid={`color-option-${i}`}
                    />
                  ))}
                </div>
              </div>

              {/* Size Selection */}
              {product.category === "apparel" && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-gray-900">Size</span>
                    <button 
                      onClick={() => setShowSizeGuide(true)}
                      className="text-sm text-purple-600 hover:underline flex items-center gap-1"
                    >
                      <Ruler className="w-4 h-4" />
                      Size Guide
                    </button>
                  </div>
                  <div className="grid grid-cols-6 gap-2">
                    {sizes.map((size, i) => (
                      <button
                        key={size}
                        onClick={() => setSelectedSize(i)}
                        className={`py-3 text-sm font-medium rounded-lg border transition-all ${
                          selectedSize === i
                            ? "bg-gray-900 text-white border-gray-900"
                            : "bg-white text-gray-700 border-gray-200 hover:border-gray-300"
                        }`}
                        data-testid={`size-option-${size}`}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* CTA Buttons */}
              <div className="space-y-3 pt-4">
                <Button 
                  className="w-full h-14 bg-gray-900 hover:bg-gray-800 text-white text-lg font-medium"
                  data-testid="add-to-cart-button"
                >
                  <ShoppingBag className="w-5 h-5 mr-2" />
                  Contact to Order
                </Button>
                <p className="text-xs text-center text-gray-500">
                  Custom orders typically ship within 2-3 weeks
                </p>
              </div>

              {/* Product Details */}
              <div className="border-t pt-6">
                <h3 className="text-sm font-medium text-gray-900 mb-3">Product Details</h3>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>• Premium quality materials</li>
                  <li>• Custom {brandName} design</li>
                  <li>• Machine washable</li>
                  <li>• Ships worldwide</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Size Guide Modal */}
        <AnimatePresence>
          {showSizeGuide && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-white z-20 p-8 overflow-y-auto"
            >
              <button
                onClick={() => setShowSizeGuide(false)}
                className="absolute right-4 top-4"
              >
                <X className="w-6 h-6" />
              </button>
              
              <h3 className="font-display text-xl font-semibold mb-6">Size Guide</h3>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4">Size</th>
                      <th className="text-left py-3 px-4">Chest (cm)</th>
                      <th className="text-left py-3 px-4">Length (cm)</th>
                      <th className="text-left py-3 px-4">Sleeve (cm)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { size: "XS", chest: "86-91", length: "66", sleeve: "60" },
                      { size: "S", chest: "91-96", length: "69", sleeve: "62" },
                      { size: "M", chest: "96-101", length: "72", sleeve: "64" },
                      { size: "L", chest: "101-106", length: "74", sleeve: "66" },
                      { size: "XL", chest: "106-111", length: "76", sleeve: "68" },
                      { size: "XXL", chest: "111-116", length: "78", sleeve: "70" },
                    ].map((row) => (
                      <tr key={row.size} className="border-b">
                        <td className="py-3 px-4 font-medium">{row.size}</td>
                        <td className="py-3 px-4">{row.chest}</td>
                        <td className="py-3 px-4">{row.length}</td>
                        <td className="py-3 px-4">{row.sleeve}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              <div className="mt-6">
                <Button onClick={() => setShowSizeGuide(false)} className="w-full">
                  Close Size Guide
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}
